import { Group, Text, Title, Stack } from "@mantine/core"
import { IconDotsVertical } from "@tabler/icons-react"
import { InformationRequestStatus } from "@tfg/db/browser"
import {
  Button,
  CopyButton,
  SectionHeader,
  Menu,
  StatusBadge,
  BadgeVariant,
} from "@tfg/ui"
import { ReactElement, useEffect } from "react"
import { useTranslation } from "react-i18next"

import { ReopenRequestModal } from "~/pages/SupplierPortal/TraceabilityRequests/Modals/ReopenRequestModal"
import { RequestItemInformation } from "~/pages/SupplierPortal/TraceabilityRequests/components/RequestItemInformation"
import { useInformationRequestContext } from "~/pages/SupplierPortal/TraceabilityRequests/store/InformationRequestContext"

type AdminInformationRequestButtonsProps = {
  status: InformationRequestStatus
  requestId: number
  productId: number
  handleSetStatusOpen: (status: InformationRequestStatus) => void
  handleReopenClick: (mode: "clean" | "dirty") => void
}

const AdminInformationRequestButtons = ({
  status,
  requestId,
  productId,
  handleSetStatusOpen,
  handleReopenClick,
}: AdminInformationRequestButtonsProps) => {
  const { reopened, reopenClose, reopenRequestMode } =
    useInformationRequestContext()
  return (
    <Menu>
      <Menu.Target>
        <IconDotsVertical size={18} />
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Admin Options</Menu.Label>
        <Menu.Item>
          <CopyButton
            label="Information Request Id"
            copyableText={requestId.toString()}
            admin={true}
            size="xs"
          />
        </Menu.Item>
        <Menu.Item>
          <CopyButton
            label="Product Id"
            copyableText={productId.toString()}
            admin={true}
            size="xs"
          />
        </Menu.Item>
        {status === InformationRequestStatus.OPENED ? (
          <>
            <Menu.Item>
              <Button
                onClick={() => handleSetStatusOpen("CLOSED")}
                variant="admin"
                fullWidth={true}
                size="xs"
              >
                CLOSE
              </Button>
            </Menu.Item>
            <Menu.Item>
              <Button
                onClick={() => handleSetStatusOpen("CANCELED")}
                variant="admin"
                size="xs"
                fullWidth={true}
              >
                CANCEL
              </Button>
            </Menu.Item>
          </>
        ) : (
          <>
            <Menu.Item>
              <Button
                onClick={() => handleReopenClick("clean")}
                variant="admin"
                fullWidth
              >
                <Text>REOPEN & CANCEL CHILDREN</Text>
              </Button>
            </Menu.Item>
            <Menu.Item>
              <Button
                onClick={() => handleReopenClick("dirty")}
                variant="admin-tertiary"
                fullWidth
              >
                REOPEN
              </Button>
            </Menu.Item>
          </>
        )}
      </Menu.Dropdown>
      <ReopenRequestModal
        requestId={requestId}
        opened={reopened}
        onClose={reopenClose}
        mode={reopenRequestMode}
      />
    </Menu>
  )
}

interface InformationRequestSummaryProps {
  setIrsToSubmit: React.Dispatch<React.SetStateAction<Set<number>>>
}

export const InformationRequestSummary = ({
  setIrsToSubmit,
}: InformationRequestSummaryProps) => {
  const {
    canSubmitIR,
    request,
    status,
    isAdmin,
    handleSetStatusOpen,
    handleReopenClick,
  } = useInformationRequestContext()
  const title: ReactElement = (
    <InformationRequestSummaryTitle
      reference={request.original.item.reference}
      status={status}
      canSubmitIR={canSubmitIR}
    />
  )

  useEffect(() => {
    setIrsToSubmit((prev: Set<number>) => {
      if (canSubmitIR && status === InformationRequestStatus.OPENED) {
        prev.add(request.original.id)
      } else {
        prev.delete(request.original.id)
      }
      return prev
    })
  }, [request.original.id, setIrsToSubmit, canSubmitIR, status])

  return (
    <Stack h="fit-content">
      <SectionHeader
        pb={0}
        title={title}
        mainCta={
          <Group wrap="nowrap">
            {isAdmin && (
              <AdminInformationRequestButtons
                status={status}
                requestId={request.original.id}
                handleSetStatusOpen={handleSetStatusOpen}
                handleReopenClick={handleReopenClick}
                productId={request.original.productId ?? 0}
              />
            )}
          </Group>
        }
      />
      <Stack pb="sm">
        <RequestItemInformation
          request={request.original}
          issuer={request.original.issuer}
        />
      </Stack>
    </Stack>
  )
}

type InformationRequestSummaryTitleProps = {
  reference: string
  status: InformationRequestStatus
  canSubmitIR: boolean
}

export const InformationRequestSummaryTitle = ({
  reference,
  status,
  canSubmitIR,
}: InformationRequestSummaryTitleProps): ReactElement => {
  const { t } = useTranslation("traceability")
  const statusBadges: Record<InformationRequestStatus, ReactElement> = {
    [InformationRequestStatus.OPENED]: (
      <StatusBadge
        fw={600}
        size="sm"
        w="100%"
        icon={null}
        text={
          canSubmitIR ? t("request.readyToSubmit") : t("request.incomplete")
        }
        variant={canSubmitIR ? BadgeVariant.WARNING : BadgeVariant.ERROR}
      />
    ),
    [InformationRequestStatus.CLOSED]: (
      <StatusBadge
        size="sm"
        fz="xs"
        fw={600}
        w="100%"
        icon={null}
        variant={BadgeVariant.SUCCESS}
        text={t("requests.tabs.closed")}
      />
    ),
    [InformationRequestStatus.CANCELED]: (
      <StatusBadge
        size="sm"
        fz="xs"
        fw={600}
        w="100%"
        icon={null}
        text={t("requests.tabs.canceled")}
        variant={BadgeVariant.ADMIN}
      />
    ),
  }
  return (
    <Group wrap="nowrap">
      <Title order={3} style={{ whiteSpace: "nowrap" }}>
        {reference}
      </Title>
      <Group justify="center" w="100%">
        {statusBadges[status]}
      </Group>
    </Group>
  )
}
